// Story 6.31 (ADO-1253) — shared SiteHeader chrome wiring.
//
// The favourites badge + depot selector are identical on every screen that
// renders the full SiteHeader (Products, My Account, Admin User Management); the
// search-to-catalogue navigation is identical on the two non-catalogue screens.
// Before this hook the wiring was duplicated almost verbatim across all three and
// drifted independently (PR #148 review). Pulling it here keeps every header in
// lock-step: one place to change the depot mapping, the favourites source, or the
// catalogue handover. (Browse is NOT wired here: it opens the department mega-menu
// in place on every screen — see useBrowseMegaMenu — so each screen owns its own
// Browse disclosure state.)
//
// Two entry points:
//   • useHeaderChrome()   — favourites + depot props, shared by ALL three screens.
//   • useCatalogueHeader() — the FULL prop bundle for a screen whose header only
//     LINKS to the catalogue: chrome + a search box that hands its term to
//     /products, + Shop/logo returning to /products. Its one consumer is
//     CatalogueLinkingHeader, which adds the typeahead + scan wiring and is what
//     those screens (PDP, My Account, Previous orders, Admin users) render. The
//     catalogue page itself does NOT use this (its search runs inline) — it
//     composes useHeaderChrome() with its own logic.

import { createElement, type ReactNode, useCallback, useMemo, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { useFavouritesStore } from '../../favourites/useFavouritesStore';
import { depotNameFor, useSessionStore } from '../../auth/useSessionStore';
import { useCartPanel, type CartPanelTrigger } from '../../basket/useCartPanel';
import { useActiveBasket } from '../../basket/useActiveBasket';
import { useBasketStore } from '../../basket/useBasketStore';
import type { CutOffPillSize, SiteHeaderDepotOption } from '../../design-system';
import { depotAccent, depotIconSrc, type DepotAccent } from '../Choose/depotAccent';
import {
  CHOOSE_ROUTE,
  LANDING_ROUTE,
  PRODUCT_DETAIL_ROUTE,
  accountLine,
  namesItsAccount,
  choiceId,
  choiceOptions,
  destinationFor,
  landingTypeFor,
  singleOrderingTarget,
} from '../../ordering/orderingChoices';
import { reloadOrderingChoices, selectOrderingChoice } from '../../ordering/orderingSession';
import { useDepotCutOff } from '../../basket/useDepotCutOff';
import { useHhtWaitingCountStore } from '../../hht/useHhtWaitingCountStore';
import { sessionCartKey, useCartTargetAt } from '../../basket/sessionCart';
import { SwitcherOptionCutOff } from './SwitcherOptionCutOff';
import type { SiteHeaderProps } from '../../design-system';
import type {
  OrderingAccount,
  OrderingChoices,
  OrderingEntry,
} from '../../api/ordering-choices.client';

/** The account chip's name: the active Trading Account's name, else its number; undefined
 *  before a choice (the chip then reads "Account"). Shared with the chooser header (DV-37). */
export function headerAccountName(
  account: Pick<OrderingAccount, 'accountName' | 'customerAccountCode'> | null,
): string | undefined {
  return account === null ? undefined : account.accountName.trim() || account.customerAccountCode;
}

/** The ordering entry the session is on, or null before the choices load (or for a user
 *  who has none). The one lookup behind the header's depot name and the depot accent. */
function activeEntryOf(
  orderingChoices: OrderingChoices | null,
  activeEntryKey: string | null,
): OrderingEntry | null {
  return orderingChoices?.entries.find((entry) => entry.key === activeEntryKey) ?? null;
}

/**
 * ADO-1779: the accent of the depot the header names (`depotAccent`), for a screen that
 * draws that depot in its own colour and icon, such as the Previous Orders depot pill.
 * `undefined` exactly when the header's name does not come from an ordering entry
 * (before the choices load, or for a user without them).
 */
export function useActiveDepotAccent(): DepotAccent | undefined {
  const orderingChoices = useSessionStore((s) => s.orderingChoices);
  const activeEntryKey = useSessionStore((s) => s.activeEntryKey);
  const activeEntry = activeEntryOf(orderingChoices, activeEntryKey);
  return activeEntry === null ? undefined : depotAccent(activeEntry);
}

/** The favourites + depot props every full-SiteHeader screen passes identically,
 *  plus the cart trigger + side panel (Story 8.2, ADO-2011), whose basket the hook
 *  keeps loaded for the session depot. */
export interface HeaderChrome extends CartPanelTrigger {
  favouritesCount: number;
  /** The switcher options: once the ordering choices have loaded, one per entry and
   *  Trading Account (Story 8.1, ADO-2010); before that, one per session depot. */
  depots: SiteHeaderDepotOption[];
  /** The active option's id (a `depots[].id`). */
  activeDepotId: string | null;
  onSelectDepot: (id: string | null) => void;
  depotName: string | undefined;
  /** Story 8.1 (ADO-2010), A9.1 — the active account line where the entry names its account
   *  (`namesItsAccount`; Story 8.4, ADO-2013). */
  depotDetail: string | undefined;
  /** Story 8.1 (ADO-2010), A9.1 / A9.3 — "Depots" to the choice screen, "Home" for a
   *  single-account landing Customer, or `null` (hidden) for any other single-account one. */
  depotsNav: { label: 'Depots' | 'Home'; to: string } | null;
  /** Story 8.1 (ADO-2010), A9.3: false for a single-account landing Customer with no
   *  other cart to switch to. */
  showDepotSwitcher: boolean;
  /** The account chip's name: the active Trading Account's name, or its number when the
   *  name is blank; `undefined` with no active account (staff, or none chosen yet). */
  accountName: string | undefined;
  /** Story 8.5 (ADO-2014), AC1 "browse the range": the session cart's cut-off for the
   *  header chip. `undefined` with no active depot; a `null` status while loading or on
   *  error, and NONE, both render nothing (AC4). */
  depotCutOff: SiteHeaderProps['depotCutOff'];
  /** Story 8.5 (ADO-2014), D-20: each switcher option's cut-off pill for its depot, at the
   *  size the panel asks for; `null` for an id the switcher does not list. */
  renderOptionCutOff: (optionId: string, size: CutOffPillSize) => ReactNode;
  /** Brand logo (Story 6.35 / ADO-1693) — the tenant's logo + accessible name. */
  logo: { src: string; alt: string };
  /**
   * Whether the tenant is themed (Story 6.35 design update, ADO-1693). Drives the
   * white brand header: a themed tenant lifts its logo into a white bar above the
   * coloured nav bar; the default ShopLink brand keeps the logo in the coloured
   * bar with no white header.
   */
  themed: boolean;
  /** Story 8.10 (ADO-2019), AC1 / AC2 / DV-39: the "HHT Orders" link, only where the
   *  workflow applies (from the HHT waiting count store; a failed read leaves it off). */
  hhtOrders: SiteHeaderProps['hhtOrders'];
}

export function useHeaderChrome(): HeaderChrome {
  const favouritesCount = useFavouritesStore((s) => s.favouriteSkus.size);
  const depotIds = useSessionStore((s) => s.depotIds);
  const activeDepot = useSessionStore((s) => s.activeDepot);
  const setActiveDepot = useSessionStore((s) => s.setActiveDepot);
  const depotNames = useSessionStore((s) => s.depotNames);
  const brand = useSessionStore((s) => s.brandConfig);
  // DV-39: the cheap count only, so a failure of the classified details never hides it.
  const hhtApplies = useHhtWaitingCountStore((s) => s.applies);
  const hhtWaitingCount = useHhtWaitingCountStore((s) => s.waitingCount);
  const orderingChoices = useSessionStore((s) => s.orderingChoices);
  const activeEntryKey = useSessionStore((s) => s.activeEntryKey);
  const activeTradingAccount = useSessionStore((s) => s.activeTradingAccount);
  const activeAccountCode = activeTradingAccount?.customerAccountCode ?? null;
  // The live basket has answered: it loaded, or its read failed. A failed first read never
  // sets `hydrated` (and every depot switch resets it), so the error counts on its own.
  const basketSettled = useBasketStore((s) => s.hydrated || s.error);
  // The live basket's line count, or null when its read failed (no badge, never a false count).
  const basketLineCount = useBasketStore((s) => (s.error ? null : s.lines.length));
  const cart = useCartPanel();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Story 8.2 (ADO-2011), AC5: the cart trigger, side panel and depot selector are
  // in every full header, so the header owns the active-basket lifecycle (open the
  // session depot's DRAFT basket, hydrate it, re-open and re-hydrate on a switch).
  // Each screen mounts exactly one header, so the basket is read once per screen
  // entry and once per depot change, and no screen mounts the hook itself.
  useActiveBasket();

  // Story 8.1 (ADO-2010), Task 6.1 — once the ordering choices have loaded, the
  // switcher lists every entry and Trading Account with its open-cart lines (A9.1; Story 8.4,
  // ADO-2013: one row and one cart per Value Centre account); the
  // active cart's count is the live basket once it has hydrated (none when its read
  // failed), since the choices read is a snapshot, taken at sign-in, by the choice screen and
  // after a switcher pick that changes the cart (`onSelectDepot`). Before the choices read lands
  // (or when it failed) the switcher falls back to the session depots, as before.
  const options = useMemo(
    () => (orderingChoices === null ? null : choiceOptions(orderingChoices)),
    [orderingChoices],
  );
  const depots = useMemo<SiteHeaderDepotOption[]>(
    () =>
      options === null
        ? depotIds.map((id) => ({ id, name: depotNameFor(depotNames, id) }))
        : options.map((option) => ({
            id: option.id,
            name: option.title,
            detail: option.detail,
            // Story 8.4 (ADO-2013): the live count belongs to the session cart, the depot AND
            // the account, so at a Value Centre only the chosen account's row shows it.
            // Enabler E8-G (ADO-3109), DA-8: and the entry, so at depot 25 only the active one
            // of Chill and Fruit & Veg (two carts) shows it. DA-10: a staff row names no account
            // (the store account is picked apart), so the active staff row shows it.
            lineCount:
              basketSettled &&
              option.entry.depotId === activeDepot &&
              option.entryKey === activeEntryKey &&
              (option.account === null || option.customerAccountCode === activeAccountCode)
                ? basketLineCount
                : option.lineCount,
            iconSrc: depotIconSrc(depotAccent(option.entry)),
          })),
    [
      options,
      depotIds,
      depotNames,
      basketSettled,
      activeDepot,
      activeEntryKey,
      activeAccountCode,
      basketLineCount,
    ],
  );
  const activeEntry = activeEntryOf(orderingChoices, activeEntryKey);
  // A switcher pick applies the destination rule (A10.1) where it matters: a pick whose
  // account has a landing page goes there, and a pick made ON the landing page goes to
  // the new account's destination (another landing type, or the product list). A product page
  // leaves for that destination itself on an entry change (`ProductDetail`), replacing itself in
  // history, so the pick does not navigate there as well. Anywhere else the Shopper stays on the
  // page (park-and-switch in place). Enabler E8-G (ADO-3109),
  // DA-10: a staff pick of another entry at the same depot (Chill to Fruit & Veg) keeps the
  // store account picked there; a pick at another depot drops it (`chooseEntry`), and the
  // browsing gate then sends the staff user to the choice screen to pick one.
  // A pick that changes the cart re-reads the choices in the background, so the row of the cart
  // left counts what this session added there; the rows keep their counts until it lands. The read
  // waits for the writes still in flight for that cart, so it cannot reach the server first.
  const onSelectDepot = useCallback(
    (id: string | null) => {
      if (options === null || id === null) {
        setActiveDepot(id);
        return;
      }
      const option = options.find((candidate) => candidate.id === id);
      if (option === undefined) return;
      const cartLeft = sessionCartKey();
      if (!selectOrderingChoice(option.entryKey, option.customerAccountCode)) return;
      if (sessionCartKey() !== cartLeft) {
        void useBasketStore
          .getState()
          .writesSettled()
          .then(() => reloadOrderingChoices({ background: true }));
      }
      const destination = destinationFor(useSessionStore.getState().activeTradingAccount);
      const applies = destination === LANDING_ROUTE || pathname === LANDING_ROUTE;
      const productPageLeaves =
        matchPath(PRODUCT_DETAIL_ROUTE, pathname) !== null &&
        useSessionStore.getState().activeEntryKey !== activeEntryKey;
      if (applies && !productPageLeaves && destination !== pathname) navigate(destination);
    },
    [options, setActiveDepot, navigate, pathname, activeEntryKey],
  );
  // A Customer with a single ordering target has no depot to choose, so the header offers
  // no "Depots" (the choice screen sends them on anyway); one whose account has a landing
  // page gets "Home" in its place (Figma 5081:54377, A9.3). Only that landing Customer
  // loses the depot tab, and only when there is nothing else to switch to. Enabler E8-G
  // (ADO-3109), DA-8: every entry is a cart of its own (a Chill account's Fruit & Veg, an
  // Ambient account's WOW), so such a Customer has more than one target and keeps both. Any
  // other single-option Customer keeps the tab with its depot name, static because there is
  // one option.
  const singleTarget = orderingChoices === null ? null : singleOrderingTarget(orderingChoices);
  const singleLandingCustomer =
    singleTarget !== null && landingTypeFor(singleTarget.account) !== null;
  const nothingToSwitchTo = singleLandingCustomer && options?.length === 1;
  // Story 8.1 (ADO-1092), AC1 / D-3 — respect the depot-selection gate: do NOT
  // re-default a multi-depot user to `depotIds[0]` here (that would undo the store
  // dropping the auto-default). Fall back to the single depot ONLY when there is
  // exactly one — otherwise `null` until the user explicitly picks, which is what
  // blocks Add and drives the header "Select a depot" prompt.
  const activeDepotId = activeDepot ?? (depotIds.length === 1 ? (depotIds[0] ?? null) : null);
  const activeOptionId =
    options === null
      ? activeDepotId
      : activeEntry === null
        ? null
        : // A staff entry offers no accounts, so its row is the entry alone (DA-10).
          choiceId(
            activeEntry.key,
            activeEntry.accounts.length === 0
              ? null
              : (activeTradingAccount?.customerAccountCode ?? null),
          );
  // Story 8.5 (ADO-2014): the real cut-off for the active depot replaces the placeholder.
  // Story 8.4 (ADO-2013): read for the active account there (each Value Centre account has its
  // own cut-off).
  const depotCutOff = useDepotCutOff(useCartTargetAt(activeDepotId));
  // Story 8.5 (ADO-2014), D-20: an option's id is its depot id until the ordering choices
  // load, then an entry + account id whose entry names the depot. Story 8.4 (ADO-2013): the
  // option's cut-off is read for its own account.
  const renderOptionCutOff = useCallback(
    (optionId: string, size: CutOffPillSize): ReactNode => {
      const option = options?.find((candidate) => candidate.id === optionId);
      const cart =
        options === null
          ? depotIds.includes(optionId)
            ? { depotId: optionId, customerAccountCode: null }
            : null
          : option === undefined
            ? null
            : { depotId: option.entry.depotId, customerAccountCode: option.customerAccountCode };
      return cart === null ? null : createElement(SwitcherOptionCutOff, { cart, size });
    },
    [options, depotIds],
  );

  return {
    ...cart,
    favouritesCount,
    depots,
    activeDepotId: activeOptionId,
    onSelectDepot,
    depotName:
      activeEntry !== null
        ? activeEntry.label
        : activeDepotId
          ? depotNameFor(depotNames, activeDepotId)
          : undefined,
    depotDetail:
      activeEntry !== null && namesItsAccount(activeEntry) && activeTradingAccount !== null
        ? accountLine(activeTradingAccount)
        : undefined,
    depotsNav: singleLandingCustomer
      ? { label: 'Home', to: LANDING_ROUTE }
      : singleTarget !== null
        ? null
        : { label: 'Depots', to: CHOOSE_ROUTE },
    showDepotSwitcher: !nothingToSwitchTo,
    accountName: headerAccountName(activeTradingAccount),
    depotCutOff: activeDepotId ? depotCutOff : undefined,
    renderOptionCutOff,
    logo: { src: brand.logoUrl, alt: brand.name },
    themed: brand.themed,
    hhtOrders: hhtApplies ? { waitingCount: hhtWaitingCount } : undefined,
  };
}

/** The base SiteHeader prop bundle for a catalogue-linking screen, spread onto
 *  <SiteHeader/> by CatalogueLinkingHeader. */
export interface CatalogueHeader extends HeaderChrome {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;
  onClearSearch: () => void;
  onNavigateHome: () => void;
}

export function useCatalogueHeader(): CatalogueHeader {
  const chrome = useHeaderChrome();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');

  // Submitting hands the term to the catalogue, which seeds + runs it on mount
  // (Products reads `state.searchTerm`); an empty box just opens the catalogue.
  const onSearchSubmit = useCallback(() => {
    const term = searchValue.trim();
    navigate('/products', term === '' ? undefined : { state: { searchTerm: term } });
  }, [navigate, searchValue]);

  // Shop/logo returns to the catalogue. (Browse used to route here too, but now
  // opens the mega-menu in place on every screen — see useBrowseMegaMenu.)
  const goToCatalogue = useCallback(() => navigate('/products'), [navigate]);

  return {
    ...chrome,
    searchValue,
    onSearchChange: setSearchValue,
    onSearchSubmit,
    onClearSearch: () => setSearchValue(''),
    onNavigateHome: goToCatalogue,
  };
}
